import { ChangeEventHandler, useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';

import {
  EntertainerQuoteReview,
  ScheduleEntertainerQuotePayload,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Popup } from '@components/layout/Popup/Popup';

export const DEFAULT_QUOTE_FONT_SIZE = 48;

export type QuotePreviewUrlFn = ({
  quoteId,
  quoteFontSize,
}: {
  quoteId: string;
  quoteFontSize: number;
}) => string;

export const QuotePreviewContent = <QuoteSource extends unknown>({
  review,
  previewUrl,
  quoteFontSize,
  onQuoteFontSizeChange,
}: {
  review: EntertainerQuoteReview<QuoteSource>;
  previewUrl: QuotePreviewUrlFn;
  quoteFontSize: number;
  onQuoteFontSizeChange: (quoteFontSize: number) => void;
}) => {
  const debouncedFontSizeChange = useMemo(
    () => debounce(onQuoteFontSizeChange, 300),
    [onQuoteFontSizeChange],
  );

  useEffect(() => () => debouncedFontSizeChange.cancel(), [debouncedFontSizeChange]);

  const handleQuoteFontSizeChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const numberValue = Number(e.target.value);
    if (!e.target.value.trim() || isNaN(numberValue)) {
      return;
    }
    debouncedFontSizeChange(numberValue);
  };

  return (
    <>
      <img
        src={review.quoteId ? previewUrl({ quoteId: review.quoteId, quoteFontSize }) : ''}
        alt="Quote preview"
        style={{ display: 'block', width: '100%' }}
      />

      <FormLabel text="Quote Font Size" />
      <Input defaultValue={quoteFontSize} onChange={handleQuoteFontSizeChange} />
    </>
  );
};

interface EntertainerQuotePreviewPopupContentProps<QuoteSource> {
  review: EntertainerQuoteReview<QuoteSource>;
  previewUrl: QuotePreviewUrlFn;
  onClose: () => void;
  onSchedule: (payload: ScheduleEntertainerQuotePayload) => Promise<void>;
}

interface EntertainerQuotePreviewPopupProps<
  QuoteSource,
> extends EntertainerQuotePreviewPopupContentProps<QuoteSource> {
  isVisible: boolean;
}

const PopupContent = <QuoteSource extends unknown>({
  review,
  previewUrl,
  onClose,
  onSchedule,
}: EntertainerQuotePreviewPopupContentProps<QuoteSource>) => {
  const [quoteFontSize, setQuoteFontSize] = useState(
    review.curatedQuote?.quoteFontSize ?? DEFAULT_QUOTE_FONT_SIZE,
  );
  const [isSchedulingQuote, setIsSchedulingQuote] = useState(false);

  const handleScheduleQuote = async () => {
    if (!review.quoteId) {
      return;
    }

    setIsSchedulingQuote(true);

    try {
      await onSchedule({ quoteId: review.quoteId, quoteFontSize });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSchedulingQuote(false);
    }

    onClose();
  };

  return (
    <>
      <QuotePreviewContent
        review={review}
        previewUrl={previewUrl}
        quoteFontSize={quoteFontSize}
        onQuoteFontSizeChange={setQuoteFontSize}
      />

      <div className="mt-2">
        <Button disabled={isSchedulingQuote} onClick={handleScheduleQuote}>
          Schedule Post
        </Button>
      </div>
    </>
  );
};

export const EntertainerQuotePreviewPopup = <QuoteSource extends unknown>({
  isVisible,
  ...props
}: EntertainerQuotePreviewPopupProps<QuoteSource>) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose} height="full" title="Preview">
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
